import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl, type SegmentedControlItemInput } from "@msuite/ui/segmented-control";
import { HintTooltip } from "@msuite/ui/tooltip";
import type { RoleList as Roles, RoleListItem } from "@msuite/utils/types";
import { hasRoleMenu, roleDisplayName } from "@/components/citadel";
import { OrganizationSettingsRolesMenu } from "./organization-settings-roles-menu";
import { RoleRowMenu } from "./role-row-menu";

/** Props Interface */
interface RoleListProps {
	roles: Roles;
	selectedRoleId: string | null;
	onSelect: (roleId: string) => void;
}

/**
 * The left half of the roles surface: a vertical segmented control over every role, with the
 * create control above it. The active pill marks the selection visibly, and the radio group
 * underneath makes the list keyboard-operable.
 *
 * The system roles sit pinned above a divider, separated from the custom roles: the server
 * orders `isSystem` first, and the divider is drawn between the two groups here. Each item
 * carries the member count as its description and — custom roles only — the row menu as its
 * action; the system roles show a lock instead, because every menu entry would be refused.
 */
export function RoleList({ roles, selectedRoleId, onSelect }: RoleListProps) {
	/** Context */
	const { t } = useTranslation();

	/** Functions */
	function toItem(role: RoleListItem): SegmentedControlItemInput<string, string> {
		return {
			value: role.id,
			label: roleDisplayName(role),
			cyId: `role-list-item-${role.id}`,
			description: `${role.memberCount} ${t("roles.member_count")}`,
			// The protected hint sits where the custom roles carry their row menu, so the row's
			// right edge means "what you can do here" for every role alike. HintTooltip renders
			// its own help icon (children are ignored); the wrapper is the same 28px square the
			// xxs icon button occupies, which is what puts the two icons on one vertical axis.
			action: hasRoleMenu(role) ? (
				<RoleRowMenu role={role} />
			) : (
				<div className="size-7 flex-center">
					<HintTooltip label={t("roles.protected_hint")} />
				</div>
			),
		};
	}

	/** Computed */
	const systemRoles = roles.filter((role) => role.isSystem);
	const customRoles = roles.filter((role) => !role.isSystem);
	const items: Array<SegmentedControlItemInput<string, string> | "divider"> = [
		...systemRoles.map(toItem),
		...(systemRoles.length && customRoles.length ? (["divider"] as const) : []),
		...customRoles.map(toItem),
	];

	/** Render */
	return (
		<div className="gap-3 min-h-0 v-stack">
			<div className="items-center justify-between gap-2 h-stack">
				<p className="font-semibold">{t("roles.roles")}</p>
				<OrganizationSettingsRolesMenu />
			</div>
			<div className="flex-1 min-h-0 overflow-y-auto">
				<SegmentedControl
					orientation="vertical"
					value={selectedRoleId ?? ""}
					onChange={onSelect}
					items={items}
				/>
			</div>
		</div>
	);
}
